/**
 * CollapsibleContent — reusable height-based clip + Show more affordance.
 *
 * Wraps arbitrary children with a collapsed/expanded affordance driven by
 * DOM measurement, mirroring the pattern that timeline email events use
 * inline (line-clamp-3 + mask fade + Show more toggle):
 *
 *   - Caps content at `maxCollapsedHeight` when collapsed (overflow hidden).
 *   - Compares `scrollHeight` against the cap to decide whether content is
 *     actually overflowing — independent of value type, so it works for
 *     plain text, markdown, multi-value chips, custom renderers, etc.
 *   - Fades the clipped body to transparency with a CSS mask (not an
 *     opaque overlay) and renders the "Show more / Show less" toggle only
 *     when overflow is actually present, so we never show a button that
 *     does nothing when clicked.
 *
 * Use this for any timeline event body whose content might or might not
 * overflow. For pure text where char-based truncation already works, prefer
 * passing `maxLength` to the underlying value renderer.
 */

import { ChevronDown, ChevronUp } from '@untitledui/icons';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';

import { useResizeObserver } from '@/hooks/useResizeObserver';

interface CollapsibleContentProps {
  children: ReactNode;
  /**
   * Maximum visible height in pixels when collapsed.
   * Defaults to 96px (~6 lines of small body text).
   */
  maxCollapsedHeight?: number;
  /**
   * Whether to render the "Show more" / "Show less" toggle button when
   * content overflows. Defaults to `true` (the original behaviour).
   *
   * Set to `false` on surfaces where the parent already provides a
   * navigational affordance to see the full content — e.g. pipeline cards
   * where clicking the card opens the entity's slideout. The clip + fade
   * still render so the truncation is visually obvious, but no in-card
   * button competes with the card's click target.
   */
  showToggle?: boolean;
  /**
   * Stable identity of the content this instance renders. When it changes,
   * the expand/collapse state resets to collapsed.
   *
   * A mounted instance can be reused for new content at the same DOM
   * position (a live property update, list virtualization recycling a row,
   * etc.). Pass a value derived from the underlying data — a record id, the
   * property value, or a combination — so the reset keys off the *content*.
   * Deliberately NOT keyed off `children` reference identity: a ReactNode
   * gets a fresh reference on every parent render, so keying on it would
   * collapse expanded content on unrelated rerenders. Omit it when the
   * instance is never reused for different content.
   */
  resetKey?: string | number;
}

const DEFAULT_MAX_HEIGHT_PX = 96;

export function CollapsibleContent({
  children,
  maxCollapsedHeight = DEFAULT_MAX_HEIGHT_PX,
  showToggle = true,
  resetKey,
}: CollapsibleContentProps) {
  const contentId = useId();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const prevResetKeyRef = useRef(resetKey);

  const handleToggle = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsExpanded(prev => !prev);
  }, []);

  // scrollHeight reflects intrinsic content height regardless of any CSS
  // max-height applied to the element, so this comparison is independent
  // of the current expanded state. We re-run on resize (via observer) and
  // when children or maxCollapsedHeight change.
  const checkOverflow = useCallback(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const overflow = el.scrollHeight > maxCollapsedHeight + 1;
    setIsOverflowing(prev => (prev === overflow ? prev : overflow));
  }, [maxCollapsedHeight]);

  useResizeObserver({ ref: containerRef, onResize: checkOverflow });
  useLayoutEffect(() => {
    // Reset expansion when the caller signals (via `resetKey`) that the
    // content this instance renders has been replaced — e.g. a reused
    // instance now showing a different value. Keyed on the explicit, stable
    // `resetKey` rather than `children` reference identity, which gets a
    // fresh reference on every parent render and would collapse expanded
    // content on unrelated rerenders.
    if (resetKey !== prevResetKeyRef.current) {
      prevResetKeyRef.current = resetKey;
      setIsExpanded(false);
    }
    // `children` stays in the deps so overflow is re-measured whenever the
    // rendered content changes, independent of whether a reset was requested.
    checkOverflow();
  }, [resetKey, children, checkOverflow]);

  // Dynamic max-height is the whole point of this wrapper, so an inline
  // style is the appropriate place for it (per the project styling rule's
  // "isolated wrapper component" exception). The mask fade below is applied
  // the same way, alongside it; only `overflow-hidden` stays a Tailwind class.
  //
  // Fade the bottom ~24px of the clipped body to transparency. Using a mask
  // (not an opaque overlay) fades the glyphs themselves — no gray band, and
  // it reveals whatever surface is actually behind the text (background-
  // independent, works in dark mode without hard-coding a color token).
  const FADE_HEIGHT_PX = 24;
  const showFade = !isExpanded && isOverflowing;
  const fadeGradient = `linear-gradient(to bottom, black calc(100% - ${FADE_HEIGHT_PX}px), transparent)`;
  const collapsedStyle: CSSProperties | undefined = isExpanded
    ? undefined
    : {
        maxHeight: `${maxCollapsedHeight}px`,
        ...(showFade
          ? { maskImage: fadeGradient, WebkitMaskImage: fadeGradient }
          : {}),
      };

  return (
    <>
      <div
        ref={containerRef}
        id={contentId}
        // Deliberately NOT `inert`. This is a HEIGHT clip, not a full hide
        // (unlike `lib/base/accordion/accordion.tsx`, which collapses to zero
        // height so inerting the whole subtree is correct there). Here the top
        // ~`maxCollapsedHeight` of the body stays visible while collapsed, so
        // any links in that visible portion — url/domain field values, links
        // and mentions inside MarkdownContent — must remain clickable. `inert`
        // is all-or-nothing on a subtree and there is no DOM boundary at the
        // clip line to split visible from hidden, so inerting the container
        // would kill interaction for the visible links too (the bug this
        // replaces). `overflow-hidden` already removes the clipped overflow
        // from the pointer hit area, and the "Show more" toggle reveals the
        // rest for anyone who needs it.
        className={`relative transition-[max-height] duration-200 ease-in-out ${!isExpanded ? 'overflow-hidden' : ''}`}
        style={collapsedStyle}
      >
        {children}
      </div>
      {isOverflowing && showToggle && (
        <button
          type='button'
          aria-expanded={isExpanded}
          aria-controls={contentId}
          onClick={handleToggle}
          // Match the canonical "link-color" treatment from `lib/base/buttons/button.tsx`:
          // `text-brand-secondary` and `text-brand-secondary_hover` resolve through
          // the design-system tokens defined in `assets/styles/theme.css`, which
          // already handle dark mode (brand-700 → gray-300). Underlining only on
          // hover keeps the button quiet inline; underline-offset-2 matches the
          // Button variant's spec.
          className='flex items-center gap-1 text-xs font-medium text-brand-secondary hover:text-brand-secondary_hover hover:underline underline-offset-2 mt-1 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring'
        >
          {isExpanded ? (
            <>
              Show less <ChevronUp className='w-3 h-3' />
            </>
          ) : (
            <>
              Show more <ChevronDown className='w-3 h-3' />
            </>
          )}
        </button>
      )}
    </>
  );
}
